import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { NAV_LINKS, CATEGORIES, BRAND } from "@/config";
import { POLICY_LINKS } from "@/data/policies";
import { PortalButton } from "@/components/PortalButton";

const phoneHref = `tel:${BRAND.phone.replace(/[^+\d]/g, "")}`;
const whatsappHref = `https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`;
const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BRAND.address)}`;

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-forest-deep text-cream">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <img src="/brand_logo.png" alt="Brampton Cash & Carry" className="h-14 w-auto rounded-lg bg-white px-3 py-2 object-contain" />
            </div>
            <p className="text-cream/60 text-sm mt-4 max-w-xs">{BRAND.altTagline}</p>
            <div className="mt-5">
              <PortalButton variant="butter" testId="footer-portal-btn">Login / Sign Up</PortalButton>
            </div>
          </div>

          <FooterCol title="Company" links={[
            { label: "About Us", to: "/about" },
            { label: "How It Works", to: "/how-it-works" },
            { label: "Delivery", to: "/delivery" },
            { label: "Become a Customer", to: "/become-a-customer" },
            { label: "Contact Us", to: "/contact" },
          ]} />

          <FooterCol title="Platform" links={[
            { label: "Online Ordering", to: "/online-ordering" },
            { label: "Reporting & Invoices", to: "/reporting" },
            { label: "Price Match", to: "/price-match" },
            { label: "Product Categories", to: "/product-categories" },
          ]} />

          <FooterCol title="Categories" links={CATEGORIES.slice(0, 5).map((c) => ({ label: c.name, to: `/product-categories#${c.slug}` }))} />

          <div>
            <h4 className="overline text-butter mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-cream/70">
              <li>
                <a href={mapsHref} target="_blank" rel="noreferrer" data-testid="footer-address-link" className="flex gap-2 hover:text-butter transition-colors">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-butter-deep" /> {BRAND.address}
                </a>
              </li>
              <li>
                <a href={phoneHref} data-testid="footer-phone-link" className="flex gap-2 hover:text-butter transition-colors">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-butter-deep" /> {BRAND.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} data-testid="footer-email-link" className="flex gap-2 hover:text-butter transition-colors">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-butter-deep" /> {BRAND.email}
                </a>
              </li>
              <li>
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex gap-2 hover:text-butter transition-colors">
                  <MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-butter-deep" /> WhatsApp {BRAND.whatsapp}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 grid lg:grid-cols-[auto_1fr_auto] gap-6 lg:items-center text-xs text-cream/50 font-mono-accent">
          <p>© {new Date().getFullYear()} Brampton Cash &amp; Carry. All rights reserved.</p>

          <nav aria-label="Website policies" data-testid="footer-policy-links" className="flex flex-wrap gap-x-5 gap-y-2 lg:justify-center">
            {POLICY_LINKS.map(({ slug, label }) => (
              <Link key={slug} to={`/${slug}`} data-testid={`footer-policy-link-${slug}`} className="hover:text-butter transition-colors">
                {label}
              </Link>
            ))}
          </nav>

          <a
            href="https://bluefig.ca"
            target="_blank"
            rel="noreferrer"
            data-testid="powered-by-bluefig"
            className="text-cream/60 hover:text-butter transition-colors lg:text-right"
          >
            Powered by BlueFig
          </a>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="overline text-butter mb-4">{title}</h4>
      <ul className="space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="text-cream/70 hover:text-butter transition-colors duration-200">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
